import { onAuthStateChanged, type User } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { useEffect, useState, type ReactNode } from "react";
import type { UserProfile } from "../types";
import { auth, db, initializeProtection } from "../lib/firebase";
import { AuthContext } from "./AuthContext";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => onAuthStateChanged(auth, async (nextUser) => {
    if (nextUser) {
      initializeProtection();
      const token = await nextUser.getIdTokenResult().catch(() => null);
      const owner = token?.claims.owner === true;
      setIsOwner(owner);
      setIsAdmin(owner || token?.claims.admin === true);
    } else {
      setIsAdmin(false);
      setIsOwner(false);
    }
    setLoading(Boolean(nextUser));
    setUser(nextUser);
    setProfile(null);
    if (!nextUser) setLoading(false);
  }), []);

  useEffect(() => {
    if (!user) return;

    return onSnapshot(
      doc(db, "users", user.uid),
      (snapshot) => {
        setProfile(snapshot.exists() ? snapshot.data() as UserProfile : null);
        setLoading(false);
      },
      () => {
        setProfile(null);
        setLoading(false);
      },
    );
  }, [user]);

  return <AuthContext.Provider value={{ user, profile, isAdmin, isOwner, loading }}>{children}</AuthContext.Provider>;
}
